import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PyramidLevel from '../pyramidLevel/PyramidLevel';
import { data } from '../data';
import useElementOnScreen from '../useElementOnScreen';
import {useTranslation} from "react-i18next";

const autoDelay = 5000;

export default function Pyramid () {
  const [level, setLevel] = useState(0);
  const timerRef = useRef(null);

  const {t} = useTranslation();

  const [containerRef, isVisible] = useElementOnScreen({
    root: null,
    rootMargin: '0px',
    threshold: 0.8,
  });

  const setCurrentLevel = (newLevel) => {
    setLevel(newLevel);
  };

  useEffect(() => {
    if (isVisible) {
      timerRef.current = setTimeout(() => {
        setLevel((prev) => {
          if (prev < data.length - 1) {
            return ++prev;
          }

          return 0;
        });
      }, autoDelay);
    }

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [level, isVisible]);

  return (
      <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '600px',
          }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: '40px',
            maxHeight: '560px',
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            maxWidth: '1200px',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            paddingBottom: '100px',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: '480px',
              width: '100%',
              maxWidth: '800px',
              margin: '0 auto',
              marginRight: { lg: '20px' },
            }}
            ref={containerRef}
          >
            {data.map((item, index) => {
              return (
                  <PyramidLevel
                      key={index}
                      index={index}
                      imgUrl={item.imgUrl}
                      imgUrlWithColor={item.imgUrlWithColor}
                      setCurrentLevel={setCurrentLevel}
                      level={level}
                  />
              );
            })}
          </Box>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              marginBottom: '70px',
              justifyContent: 'center',
              alignItems: 'center',
              paddingLeft: { lg: '120px' },
              flex: { lg: '1 0 60%' },
              height: { lg: '480px' },
            }}
          >
            {data.map(({ title, text }, index) => {
              return (
                  <Box
                    key={index}
                    sx={{
                      padding: { xs: '36px 32px', sm: '40px', lg: '40px 48px' },
                      width: '100%',
                      opacity: level === index ? 1 : 0,
                      marginLeft: { xs: '20px', lg: 0 },
                      transition: 'opacity 300ms ease-in, transform 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      top: { xs: '60px', lg: '25%' },
                      position: 'absolute',
                      maxWidth: '720px',
                      transform: level === index ? 'translateY(0)' : 'translateY(10px)',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '18px',
                      borderLeft: { xs: '3px solid #007AFF', lg: 'none' },
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #E5E5E7',
                    }}
                  >
                    <Box
                      sx={{
                        marginBottom: '20px',
                        overflow: 'visible',
                        minHeight: { xs: '80px', sm: '90px', lg: '100px' },
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: '32px', sm: '36px', lg: '38px' },
                          fontWeight: 600,
                          letterSpacing: '-0.02em',
                          marginBottom: '10px',
                          position: 'relative',
                          display: 'inline-block',
                          color: '#1D1D1F',
                          lineHeight: 1.2,
                          wordWrap: 'break-word',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: '-4px',
                            left: 0,
                            width: level === index ? '100%' : '40px',
                            height: '3px',
                            background: '#007AFF',
                            borderRadius: '2px',
                            transition: 'width 300ms ease',
                          },
                        }}
                      >
                        {t(title)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        maxWidth: { xs: '480px', lg: '560px' },
                        fontSize: { xs: '18px', sm: '19px', lg: '20px' },
                        lineHeight: { xs: 1.6, lg: 1.7 },
                        color: level === index ? '#1D1D1F' : '#86868B',
                        fontWeight: 400,
                        transition: 'color 300ms ease',
                      }}
                    >
                      {t(text)}
                    </Typography>
                  </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
  );
};
